import { Link, useNavigate } from "react-router";
import { motion } from "motion/react";
import { ArrowLeft } from "lucide-react";
import {
  LandingNavbar,
  NAVBAR_HEIGHT,
} from "@/components/landing/LandingNavbar";
import { Footer } from "@/components/landing/Footer";
import { FloatingSubscribe } from "@/components/landing/PulseSubscribeBar";
import type { DailyPulse } from "@altair/shared/types";
import { resolveStorySlug } from "@/lib/news/slug";
import { StoryBlock } from "./StoryBlock";
import { PulseNavigation } from "./PulseNavigation";
import { formatPulseDate } from "./payer-colors";

/**
 * Full pulse page — renders the date header and every story in `pulse.stories`.
 * This is the default `/news/:date` view. Visual design preserved from the
 * pre-split InsightDetailPage.tsx.
 */

interface FullPulseViewProps {
  pulse: DailyPulse;
  /** Recent pulse dates for prev/next (only `.date` is read). */
  allPulses: Pick<DailyPulse, "date">[];
}

function estimateReadTime(pulse: DailyPulse) {
  const words = pulse.stories.reduce(
    (sum, s) =>
      sum + s.summary.split(/\s+/).length + s.headline.split(/\s+/).length,
    0
  );
  return Math.max(2, Math.ceil(words / 200));
}

export function FullPulseView({ pulse, allPulses }: FullPulseViewProps) {
  const navigate = useNavigate();
  const navbarHeight = NAVBAR_HEIGHT;

  const readTime = estimateReadTime(pulse);

  return (
    <div className="min-h-dvh">
      <LandingNavbar
        isProduct={false}
        onBack={() => {
          // Force a full-page nav so / loads from the fresh main-webapp
          // deploy. /news is served by a separate news-renderer SSR
          // service that references an older bundle, and SPA-navigating
          // back to / would reuse that stale JS to render the old
          // landing page (e.g. pre-launch countdown navbar).
          window.location.href = "/";
        }}
      />

      <main
        className="px-5"
        style={{
          paddingTop: `max(${navbarHeight + 40}px, 12vh)`,
          paddingBottom: 96,
        }}
      >
        <div className="mx-auto" style={{ maxWidth: 680 }}>
          {/* Back link */}
          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 no-underline mb-8"
            style={{
              fontSize: 13,
              color: "hsl(var(--foreground) / 0.40)",
              transition: "color 150ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "hsl(var(--foreground))";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "hsl(var(--foreground) / 0.40)";
            }}
            onClick={(e) => {
              // Force full-page nav so news-renderer SSR serves /news with
              // fresh DB-backed content. SPA route falls through to bundled
              // samplePulses (Apr 15 cutoff).
              e.preventDefault();
              window.location.href = "/news";
            }}
          >
            <ArrowLeft size={14} />
            Billing News
          </Link>

          {/* Page header */}
          <motion.header
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "clamp(28px, 4vw, 38px)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "hsl(var(--foreground))",
                lineHeight: 1.15,
                marginTop: 10,
              }}
            >
              {formatPulseDate(pulse.date)}
            </h1>
            <p
              style={{
                fontSize: 14,
                color: "hsl(var(--foreground) / 0.40)",
                marginTop: 8,
              }}
            >
              {pulse.storyCount} stories · {readTime}-minute read
            </p>
            <div
              style={{
                borderTop: "1px solid hsl(var(--foreground) / 0.08)",
                marginTop: 24,
              }}
            />
          </motion.header>

          {/* Stories */}
          <div style={{ marginTop: 36 }}>
            {pulse.stories.map((story, idx) => {
              const storySlug = resolveStorySlug(story);
              return (
                <motion.div
                  key={storySlug || idx}
                  id={storySlug ? `story-${storySlug}` : `story-${idx}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.45,
                    ease: [0.16, 1, 0.3, 1],
                    delay: idx * 0.08,
                  }}
                  style={{
                    scrollMarginTop: navbarHeight + 24,
                  }}
                >
                  {/* Legacy anchor (same node, secondary id via span) for
                       back-compat with old #story-N URLs. */}
                  {storySlug && (
                    <span
                      id={`story-${idx}`}
                      aria-hidden="true"
                      style={{ display: "block", height: 0 }}
                    />
                  )}
                  <StoryBlock
                    story={story}
                    date={pulse.date}
                    slug={storySlug}
                    index={idx}
                  />
                  {idx < pulse.stories.length - 1 && (
                    <div
                      style={{
                        borderTop: "1px solid hsl(var(--foreground) / 0.06)",
                        margin: "40px 0",
                      }}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Previous / Next navigation */}
          <PulseNavigation currentDate={pulse.date} allPulses={allPulses} />
        </div>
      </main>

      <Footer />

      <FloatingSubscribe />
    </div>
  );
}
